import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Autocomplete } from '@react-google-maps/api';

const libraries = ["places"];

const GooglePlacesAutocomplete = ({ onPlaceSelect, locationAddress }) => {
  console.log("calling google places autocomplete");
  const [autocomplete, setAutocomplete] = useState(null);
  const inputRef = useRef(null);
  // const [sessionToken, setSessionToken] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY,
    libraries,
  });

  const onLoad = useCallback((autoC) => {
    setAutocomplete(autoC);
  }, []);

  // 👇 NEW: Function to process and format the Place details
  const processPlace = useCallback((place) => {
    console.log("Place details received:", place);
    const components = place.address_components;
    // const formattedAddress = place.formatted_address || place.name; // Use name if formatted_address is missing
    
    const addressDetails = {};
    let streetNumber = '';
    let route = '';
    
    for (const component of components) {
        const type = component.types[0];
        
        if (type === 'postal_code') {
            addressDetails.postalCode = component.long_name;
        } else if (type === 'locality' || type === 'postal_town') {
            addressDetails.city = component.long_name;
        } else if (type === 'administrative_area_level_1') {
            addressDetails.state = component.long_name;
        } else if (type === 'country') {
            addressDetails.country = component.long_name;
        }else if(type === 'route'){
            route = component.long_name;
        }else if(type === 'street_number'){
            streetNumber = component.long_name;
        }
    }
    addressDetails.streetAndNumber = `${streetNumber} ${route}`.trim();
    console.log("Processed Address Details:", addressDetails);

    onPlaceSelect(addressDetails);
    
    // Crucial for session billing: Terminate the session
    // if (token) {
    //   setSessionToken(null);
    // }
  }, [onPlaceSelect]);


  // UPDATED: onPlaceChanged now calls the processing function
  const onPlaceChanged = useCallback(() => {
    if (autocomplete !== null ) {
      const place = autocomplete.getPlace();
      
      // If the selection is valid, process it and terminate the session
      if (place.place_id) {
          processPlace(place); 
      }
    }
  }, [autocomplete, processPlace]);

  // 👇 2. UPDATED useEffect to handle both token and prefill logic
  useEffect(() => {
    if (isLoaded) {
      // Session Token Generation
      // if (!sessionToken) {
      //   const token = new window.google.maps.places.AutocompleteSessionToken();
      //   setSessionToken(token);
      // }
      
      const prefillAddress = locationAddress; // <-- Set your desired value here!
      
      // 3. MANUAL TRIGGER: Use FindPlaceFromQuery for the pre-filled address
      if (inputRef.current && inputRef.current.value === '' && prefillAddress !== null && prefillAddress !== undefined && prefillAddress !== '') { 
          inputRef.current.value = prefillAddress;

          // You can only trigger the API call if the global google object is loaded
          if (window.google && window.google.maps && window.google.maps.places) {
              
              const service = new window.google.maps.places.PlacesService(inputRef.current);
              
              // Use Find Place From Query with Field Masks
              service.findPlaceFromQuery({
                  query: prefillAddress,
                  fields: ['place_id'], // Required fields// Pass the active session token!
              }, (results, status) => {
                  if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
                      const placeId = results[0].place_id;
                      
                      // Now get the details using the Place ID
                      service.getDetails({
                          placeId: placeId,
                          fields: ['address_components'], // Keep this minimal!
                      }, (place, detailsStatus) => {
                          if (detailsStatus === window.google.maps.places.PlacesServiceStatus.OK && place) {
                              // Process the result just like in a user selection
                              processPlace(place); 
                          } else {
                              console.error("Place Details request failed:", detailsStatus);
                          }
                      });
                  } else {
                      console.error("Find Place from Query failed:", status);
                      // Since the session was used for findPlaceFromQuery, we terminate it here
                      // setSessionToken(null); 
                  }
              });
          }
      }
    }
    // Cleanup function
    // return () => {
    //     setSessionToken(null);
    // };
  }, [isLoaded, processPlace]); 
  // -------------------------------------------------------------

  if (loadError) return <div>Error loading details</div>;
  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div style={{ padding: '20px' }}>
      <Autocomplete 
        onLoad={onLoad} 
        onPlaceChanged={onPlaceChanged}
        options={{
            // Include 'formatted_address' as you use it in the component logic
            fields: ['address_components', 'formatted_address'],  
            types: ['address'] // Recommended to restrict search to addresses
        }}
      >
        <input
          ref={inputRef}
          type="text"
          placeholder="Enter an address"
          style={{
            boxSizing: `border-box`,
            border: `1px solid transparent`,
            width: `240px`,
            height: `32px`,
            padding: `0 12px`,
            borderRadius: `3px`,
            boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
            fontSize: `14px`,
            outline: `none`,
            textOverflow: `ellipses`,
          }}
        />
      </Autocomplete>
    </div>
  );
};

// Example usage in your main component
const AddressForm = ({ handleAutoCompletionSelect, locationAddress, locationDetails }) => {
    const [address, setAddress] = useState({});
    
    const handlePlaceSelect = (details) => {
        // setAddress({ ...details, formattedAddress });
        console.log("Extracted Details:", details);
        handleAutoCompletionSelect(details);
        // You can now populate your form fields with the data in 'details'
    };
    
    return (  
        <div>
            {/* <h2>Address Lookup</h2> */}
            {/* country: details.country || null,
      city: details.city || null,
      postalCode: details.postalCode || null,
      state: details.state || null,
      streetAndNumber: details.streetAndNumber || null, */}
            {locationDetails.country === null && locationDetails.city === null && locationDetails.postalCode === null &&
            locationDetails.state === null && locationDetails.streetAndNumber === null && 
            <GooglePlacesAutocomplete onPlaceSelect={handlePlaceSelect} locationAddress={locationAddress} />}
            
            {/* Display fetched data */}
            {/* {address.city && (
                <div style={{ marginTop: '15px', border: '1px solid #ccc', padding: '10px' }}>
                    <p>Formatted Address: **{address.formattedAddress}**</p>
                    <p>City: **{address.city}**</p>
                    <p>State: **{address.state}**</p>
                    <p>Postal Code: **{address.postalCode}**</p>
                </div>
            )} */}
        </div>
    );
};

export default AddressForm;